import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Non-blocking "please verify your email" banner. Shows for signed-in users
// whose email isn't verified yet, on the app's own pages. It never gates any
// action — it's a reminder with a one-click "send the link" button. Dismissal
// is remembered for the session (per uid) so it doesn't nag on every navigation.
export default function VerifyEmailBanner({ user }) {
  const location = useLocation();
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [dismissed, setDismissed] = useState(() => {
    if (!user) return false;
    try {
      return sessionStorage.getItem(`verifyBannerDismissed:${user.uid}`) === '1';
    } catch {
      return false;
    }
  });

  // Hide when: not logged in, already verified, dismissed this session, or on
  // the login / email-action pages (where it makes no sense).
  if (!user || user.emailVerified || dismissed) return null;
  const path = location.pathname;
  if (path.startsWith('/login') || path.startsWith('/auth/action')) return null;

  function dismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(`verifyBannerDismissed:${user.uid}`, '1'); } catch { /* ignore */ }
  }

  async function sendLink() {
    setStatus('sending');
    try {
      await httpsCallable(functions, 'sendVerificationEmail')();
      setStatus('sent');
    } catch (err) {
      console.error('sendVerificationEmail failed:', err);
      setStatus('error');
    }
  }

  return (
    <div className="verify-banner" role="status">
      <div className="verify-banner__text">
        {status === 'sent' ? (
          <>Verification link sent to <strong>{user.email}</strong>. Check your inbox (and spam).</>
        ) : status === 'error' ? (
          <>Couldn't send the link just now. Please try again in a moment.</>
        ) : (
          <>Confirm your email so you can recover your account if you forget your password.</>
        )}
      </div>
      <div className="verify-banner__actions">
        {status !== 'sent' && (
          <button
            type="button"
            className="verify-banner__btn"
            onClick={sendLink}
            disabled={status === 'sending'}
          >
            {status === 'sending' ? 'Sending…' : (status === 'error' ? 'Try again' : 'Send verification email')}
          </button>
        )}
        <button type="button" className="verify-banner__dismiss" onClick={dismiss} aria-label="Dismiss">
          ✕
        </button>
      </div>
    </div>
  );
}
